import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button, Input, Label } from '../../ui'
import { approveSolicitud } from '../../../lib/supabase'
import { formatMoney } from '../../../utils/helpers'
import { useAuth } from '../../../hooks/useAuth'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'

const AprobarSolicitudModal = ({ open, onOpenChange, solicitud, onSuccess }) => {
  const { staff } = useAuth()
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const [aplicaciones, setAplicaciones] = useState({}) // cargoId -> monto
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!solicitud) return
    const init = {}
    ;(solicitud.cargos || []).forEach((sc) => {
      init[sc.cargo_id] = String(sc.monto_propuesto)
    })
    setAplicaciones(init)
    setError(null)
    guard.resetDirty()
  }, [solicitud]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!solicitud) return null

  const totalAplicado = Object.values(aplicaciones).reduce((s, v) => s + (parseFloat(v) || 0), 0)
  const sobrante = Math.max(solicitud.monto_total - totalAplicado, 0)

  // El monto propuesto es el que el papa reporto ENTONCES. Si desde entonces le
  // otorgaron una promocion, el cargo vale menos y su propuesta quedo por encima
  // del saldo vigente. No truena -- lo que sobre pasa a saldo a favor -- pero el
  // aprobador tiene que verlo.
  const cargoAbaratado = (solicitud.cargos || []).some(
    (sc) => Number(sc.monto_propuesto || 0) > Number(sc.pagos_cargos?.saldo || 0)
  )

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    const detalles = (solicitud.cargos || []).map((sc) => ({
      cargo_id: sc.cargo_id,
      monto_aplicado: parseFloat(aplicaciones[sc.cargo_id] || 0),
    })).filter((d) => d.monto_aplicado > 0)

    const payload = {
      monto: solicitud.monto_total,
      metodo_pago: solicitud.metodo_pago,
      referencia: solicitud.referencia,
      fecha: solicitud.fecha_pago,
      notas: solicitud.notas,
      detalles,
      sobrante_saldo_favor: sobrante,
    }
    const { data, error: err } = await approveSolicitud(solicitud.id, {
      transaccionPayload: payload,
      staffId: staff?.id,
    })
    setSubmitting(false)
    if (err) {
      setError(err.message || 'Error al aprobar')
      return
    }
    guard.resetDirty()
    onSuccess?.(data)
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aprobar solicitud</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Monto total declarado: <strong>{formatMoney(solicitud.monto_total)}</strong>
          </p>

          <div className="space-y-2">
            <Label>Aplicar a cargos</Label>
            {(solicitud.cargos || []).map((sc) => (
              <div key={sc.cargo_id} className="flex items-center justify-between gap-2">
                <span className="text-sm flex-1 truncate">
                  {sc.pagos_cargos?.pagos_conceptos?.nombre || 'cargo'}
                  <span className="text-xs text-muted-foreground ml-1">
                    (saldo {formatMoney(sc.pagos_cargos?.saldo)})
                  </span>
                </span>
                <Input
                  type="number" step="0.01" className="w-28"
                  value={aplicaciones[sc.cargo_id] || ''}
                  onChange={(e) => setAplicaciones({ ...aplicaciones, [sc.cargo_id]: e.target.value })}
                />
              </div>
            ))}
          </div>

          {cargoAbaratado && (
            <p className="text-xs text-amber-700">
              Algún cargo bajó de precio después de que se reportó el pago. Revisa los montos:
              lo que sobre pasará a saldo a favor.
            </p>
          )}

          <div className="border-t pt-2 text-sm">
            <p>Total aplicado: <strong>{formatMoney(totalAplicado)}</strong></p>
            {sobrante > 0 && (
              <p className="text-emerald-700">Sobrante a saldo a favor: {formatMoney(sobrante)}</p>
            )}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={submitting || totalAplicado <= 0}>
            {submitting ? 'Aprobando...' : 'Confirmar aprobacion'}
          </Button>
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default AprobarSolicitudModal
